import { useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useRef } from 'react';
import { CaseStoryEmptyState } from './case-story-empty-state';
import { CaseStoryShell } from './case-story-shell';
import { buildStoryModel } from './story-model';
import { useStoryData } from './use-story-data';
import { useTimelinePosition } from './use-timeline-position';

export function CaseStoryPage() {
  const { caseId } = useParams({ strict: false }) as { caseId: string };
  const search = useSearch({ strict: false }) as { t?: string };
  const { highlights, periods, isLoading } = useStoryData(caseId);
  const model = useMemo(() => buildStoryModel(highlights, periods), [highlights, periods]);
  const deepLinkTime = parseIsoToEpoch(search.t);
  const initialPosition = deepLinkTime === null ? 0 : model.scale.toPosition(deepLinkTime);
  const engine = useTimelinePosition({ min: 0, max: model.length, initial: initialPosition });
  const deepLinkAppliedRef = useRef(false);
  const hasStoryContent = model.items.length > 0 || periods.length > 0;

  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    if (deepLinkTime === null) return;
    // En tom skala mapper alle tidspunkt til 0, så vent til modellen har innhold.
    if (model.length === 0) return;
    deepLinkAppliedRef.current = true;
    engine.setTarget(model.scale.toPosition(deepLinkTime));
  }, [deepLinkTime, engine, model.length, model.scale]);

  if (isLoading && !hasStoryContent) {
    return (
      <div className="flex h-[calc(100dvh-3rem)] items-center justify-center text-sm text-text-tertiary">
        Laster historie…
      </div>
    );
  }

  if (!hasStoryContent) {
    return <CaseStoryEmptyState caseId={caseId} />;
  }

  return <CaseStoryShell caseId={caseId} model={model} periods={periods} engine={engine} />;
}

function parseIsoToEpoch(iso: string | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return ms;
}
